import React, {FC, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory, useParams} from 'react-router-dom';

import {Button, Container, FaqDropdownCard, Input, PageTitle} from 'components';
import {useWindowDimensions} from 'hooks';
import {FaqContent, faqFilters, FaqFilterType, useFaqFilterLabel, useFaqQuestionsAndAnswers} from 'types/faq';

// import Feedback from './Feedback';
import FaqSideMenu from './FaqSideMenu';
import './Faq.scss';

const HIDE_LEFT_MENU_WIDTH = 600;

const faqFilterKeyMap = Object.values(FaqFilterType).reduce(
  (acc, filterType) => ({...acc, [filterType]: true}),
  {} as {[key: string]: boolean},
);

/** i18n 改造前 FaqFilterType 枚举值是中文并直接拼进 URL，此处保留旧值到新 slug 的映射做兼容跳转 */
const LEGACY_FILTER_SLUGS: Record<string, FaqFilterType> = {
  图片网盘: FaqFilterType.projects,
  如何使用: FaqFilterType.how,
  所有问题: FaqFilterType.all,
  水印: FaqFilterType.howTo,
  精修工具: FaqFilterType.imageedit,
};

const Faq: FC = () => {
  const history = useHistory();
  const {filter} = useParams<{filter: FaqFilterType}>();
  const [searchText, setSearchText] = useState<string>(''); // search input value
  const [searchQuery, setSearchQuery] = useState<string>(''); // what is actually being used as filter
  const {width} = useWindowDimensions();
  const {t} = useTranslation('faq');
  const faqQuestionsAndAnswers = useFaqQuestionsAndAnswers();
  const faqFilterLabels = useFaqFilterLabel();

  useEffect(() => {
    if (faqFilterKeyMap[filter]) return;
    // 旧中文 URL 先映射到新 slug，未知值回落到 all
    const legacySlug = LEGACY_FILTER_SLUGS[filter] || FaqFilterType.all;
    history.replace(`/faq/${legacySlug}`);
  }, [filter, history]);

  const filteredQnAsByFilterType = useMemo(() => {
    if (width <= HIDE_LEFT_MENU_WIDTH) {
      return faqQuestionsAndAnswers;
    }

    if (filter !== FaqFilterType.all) {
      return faqQuestionsAndAnswers.filter((faqTopic) => faqTopic.topic === filter);
    }
    return faqQuestionsAndAnswers;
  }, [faqQuestionsAndAnswers, filter, width]);

  const filteredQnAsBySearchQuery = useMemo(() => {
    const sanitizedSearch = searchQuery.trim().toLowerCase();

    if (!sanitizedSearch) {
      return filteredQnAsByFilterType;
    }

    return filteredQnAsByFilterType
      .map((faqTopic) => {
        return {
          // filter questions that match with search query
          content: faqTopic.content.filter((qna) => qna.question.toString().toLowerCase().includes(sanitizedSearch)),
          topic: faqTopic.topic,
        };
      })
      .filter((faqTopic) => faqTopic.content.length > 0); // filter types that have no questions
  }, [filteredQnAsByFilterType, searchQuery]);

  const renderQuestionsAndAnswers = () => {
    if (filteredQnAsBySearchQuery.length === 0) {
      return <div className="Faq__empty-questions">{t('empty')}</div>;
    }
    return filteredQnAsBySearchQuery.map((faqTopic) => {
      return renderTopicQuestionsAndAnswers(faqTopic.topic, faqTopic.content);
    });
  };

  const renderTopicQuestionsAndAnswers = (topic: FaqFilterType, content: FaqContent[]) => {
    return (
      <div className="Faq__topic" key={topic}>
        <div className="Faq__topic-title">{faqFilterLabels[topic]}</div>
        {content.map((qna) => (
          <FaqDropdownCard
            className="Faq__dropdown-card"
            id={qna.id}
            key={qna.id}
            question={qna.question}
            answer={qna.answer}
          />
        ))}
      </div>
    );
  };

  const handleClearClick = (): void => {
    setSearchQuery('');
    setSearchText('');
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setSearchQuery(searchText);
    }
  };

  return (
    <>
      <PageTitle canonicalUrl={`https://www.xiangjifanyi.com/home/faq/${filter}`} title={t('pageTitle')} />
      <Container className="Faq">
        <div className="Faq__title-container">
          <div className="Faq__title">{t('title')}</div>
          <div className="Faq__subtitle">{t('subtitle')}</div>
        </div>
        <div className="Faq__content">
          {width > HIDE_LEFT_MENU_WIDTH && (
            <div className="Faq__sidemenu">
              <FaqSideMenu />
            </div>
          )}
          <div className="Faq__content-main">
            <div className="Faq__search">
              <Input
                className="Faq__search-input"
                fullWidth
                value={searchText}
                placeholder={t('searchPlaceholder')}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={handleSearchKeyDown}
              />
              <Button
                className="Faq__search-clear"
                disabled={!searchText && !searchQuery}
                onClick={handleClearClick}
                variant="outlined"
              >
                {t('searchClear')}
              </Button>
              <Button disabled={!searchText} onClick={() => setSearchQuery(searchText)}>
                {t('search')}
              </Button>
            </div>
            <div>{renderQuestionsAndAnswers()}</div>
          </div>
        </div>
        {/* <Feedback /> */}
      </Container>
    </>
  );
};

export {FaqFilterType, faqFilters};

export default Faq;
